// PERF-STRESS (2026-11-02): GPU upload trimming for instanced meshes. Render-only bookkeeping; never changes what is drawn.
//
// Measured on VM 110 (Docs/QA/PERF_BASELINE_2026-11-02.md): with ~450 foes and a maxed build the frame uploaded ~7 MB of
// buffer data, almost all of it unused. Every InstancedMesh pool is sized for its worst case (enemy families for the whole
// horde cap, particle / VFX pools for thousands of instances), and three.js re-uploads the WHOLE capacity whenever a pool
// marks its matrices dirty, for every render object, even when the pool draws nothing (count 0 still runs the attribute and
// uniform-buffer updates; only the draw itself is skipped). In Chrome every byte is copied to the GPU process and replayed
// through ANGLE, which was the largest single cost in the stress frame.
//
// Per frame, between view.sync() and the render:
//  * a visible InstancedMesh with count 0 is hidden for this one render (restored right after) — it would draw nothing
//    (only once it has been rendered once, so its shader is still compiled up front and first use does not hitch);
//  * dirty per-instance attributes (instanceMatrix, instanceColor, InstancedBufferAttributes of the geometry) get an update
//    range covering only [0, count) instead of the full capacity;
//  * instanced meshes whose geometry has too many attributes are instanced by three.js through a uniform buffer holding the
//    matrices (enemy families with gait / death attributes); the backend's uniform-buffer upload is narrowed to the live
//    matrices the same way (one wrapped backend method, see install()).
// Invariant: the GPU copy is valid for [0, valid) of every tracked attribute; when a pool's count grows past `valid` without a
// new version (data written earlier, count raised later) the attribute is re-flagged so the newly drawn slots are uploaded.
// Disable with ?trim=0 for A/B.
import * as THREE from 'three/webgpu';

/** visible all the way up (so it really renders this frame) */
const shown = (o: THREE.Object3D): boolean => { for (let p: THREE.Object3D | null = o; p; p = p.parent) if (!p.visible) return false; return true; };

interface Seen { v: number; valid: number; ours: boolean }
type Attr = THREE.BufferAttribute | THREE.InstancedBufferAttribute;

export class UploadTrim {
  enabled = true;
  private meshes: THREE.InstancedMesh[] = [];
  private hidden: THREE.Object3D[] = [];
  private scanIn = 0;
  private readonly seen = new WeakMap<object, Seen>();
  /** floats of a uniform-buffer-backed instance matrix array that are drawn this frame (enemy families etc.) */
  private readonly live = new WeakMap<object, number>();
  private readonly need = new Map<Attr, number>();
  /** pools rendered at least once (shader compiled); only those are skipped while empty */
  private readonly warm = new WeakSet<object>();
  /** stats for ?stress / ?perf */
  stats = { hidden: 0, trimmed: 0, savedKB: 0 };
  /** PERF PASS 3 (title-first boot): while the full warm-up still runs in the background, EVERY empty pool is hidden for
   *  the render (not only the warm ones) so the title / menu frames never compile a pipeline; the warm-up compiles them
   *  off screen and clears this. Non-instanced strips with an empty draw range are hidden the same way. */
  deferEmpty = false;
  private empties: THREE.Object3D[] = [];

  constructor(private readonly scene: THREE.Scene) {}

  /** Narrow the backend's uniform-buffer uploads of instance matrices to the live range (WebGL2 + WebGPU backends). */
  install(renderer: THREE.WebGPURenderer): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const B = renderer.backend as any;
    if (!B || typeof B.updateBinding !== 'function') return;
    const orig = B.updateBinding.bind(B), live = this.live;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    B.updateBinding = (binding: any) => {
      const buf = binding?.buffer;
      if (this.enabled && buf && binding.isNodeUniformBuffer && binding.updateRanges?.length === 0) {
        const n = live.get(buf);
        if (n !== undefined && n < buf.length) {
          if (n <= 0) return; // the pool draws nothing this frame (it is hidden as well; kept for safety)
          binding.addUpdateRange(0, n); // Bindings clears the ranges after the update
        }
      }
      return orig(binding);
    };
  }

  /** Call after view.sync(), right before rendering. */
  before(): void {
    if (!this.enabled) return;
    if (--this.scanIn <= 0 || this.deferEmpty) { // pools are created lazily (first use of a technique), so re-scan now and then
      this.scanIn = 60; this.meshes.length = 0; this.empties.length = 0;
      this.scene.traverse((o) => {
        if ((o as THREE.InstancedMesh).isInstancedMesh) this.meshes.push(o as THREE.InstancedMesh);
        else if (this.deferEmpty) { // same predicate as Prewarm's hideEmpty(): meshes, points, lines and sprites that draw nothing
          const g = (o as THREE.Mesh).geometry as (THREE.BufferGeometry & { instanceCount?: number }) | undefined;
          if (g && (!g.attributes.position || g.drawRange.count === 0 || g.instanceCount === 0)) this.empties.push(o);
        }
      });
    }
    const need = this.need; need.clear();
    let hidden = 0;
    if (this.deferEmpty) for (const m of this.empties) { if (m.visible && m.parent) { m.visible = false; this.hidden.push(m); hidden++; } }
    for (const m of this.meshes) {
      if (!m.visible || !m.parent) continue;
      if (m.count <= 0) {
        // the first render of an (empty) pool compiles its shader; keep that so the first use of a technique does not hitch
        // (deferEmpty: the warm-up does that off screen instead, so empty pools are hidden regardless)
        if (this.warm.has(m) || this.deferEmpty) { m.visible = false; this.hidden.push(m); hidden++; continue; }
        if (shown(m)) this.warm.add(m);
      } else if (shown(m)) this.warm.add(m);
      // the larger count wins when an attribute is shared between pools (body / outline share the enemy matrices)
      const n = m.count; if (n <= 0) continue;
      const im = m.instanceMatrix as Attr; need.set(im, Math.max(need.get(im) ?? 0, n * 16));
      if (m.instanceColor) need.set(m.instanceColor, Math.max(need.get(m.instanceColor) ?? 0, n * 3));
      const attrs = m.geometry.attributes;
      for (const k in attrs) {
        const a = attrs[k] as THREE.InstancedBufferAttribute;
        if (a.isInstancedBufferAttribute && a.meshPerAttribute === 1 && !(a as unknown as THREE.InterleavedBufferAttribute).isInterleavedBufferAttribute) need.set(a, Math.max(need.get(a) ?? 0, n * a.itemSize));
      }
    }
    this.stats.hidden = hidden;
    let trimmed = 0, saved = 0;
    for (const [a, floats] of need) {
      const len = a.array.length;
      if (floats >= len) { this.seen.set(a, { v: a.version, valid: len, ours: false }); this.live.set(a.array, len); continue; }
      this.live.set(a.array, floats);
      const s = this.seen.get(a);
      if (!s) { this.seen.set(a, { v: a.version, valid: len, ours: false }); continue; } // first sighting: let three upload it whole
      if (a.version !== s.v) { // dirty: it is uploaded this frame — only the drawn part
        if (a.updateRanges.length === 0 || s.ours) { a.clearUpdateRanges(); a.addUpdateRange(0, floats); s.ours = true; trimmed++; saved += (len - floats) * 4; }
        else s.ours = false;
        s.v = a.version; s.valid = floats;
      } else if (floats > s.valid) { // more slots drawn than were uploaded since the last change: upload them now
        a.clearUpdateRanges(); a.addUpdateRange(0, floats); a.needsUpdate = true;
        s.v = a.version; s.valid = floats; s.ours = true; trimmed++;
      }
    }
    this.stats.trimmed = trimmed; this.stats.savedKB = Math.round(saved / 1024);
  }

  /** Call right after rendering: restores the pools hidden for the render. */
  after(): void {
    for (const o of this.hidden) o.visible = true;
    this.hidden.length = 0;
  }
}

/** Dynamic strip / ribbon buffers: flag only the first `items` vertices of each attribute for upload (nothing when 0 — the
 *  draw range is empty then). Same data on screen; the rest of the pooled buffer is not re-sent every frame. */
export function uploadPrefix(items: number, ...attrs: THREE.BufferAttribute[]): void {
  if (items <= 0) return;
  for (const a of attrs) { a.clearUpdateRanges(); a.addUpdateRange(0, Math.min(a.array.length, items * a.itemSize)); a.needsUpdate = true; }
}
